import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const AccountPage = () => {
  const { user, token } = useAuth();
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  useEffect(() => {
    const fetchTokenData = async () => {
      try {
        // Log existing user data from context
        console.log("Auth Context User:", user);
        
        // Fetch token usage data
        const tokenResponse = await fetch('/api/users/token-usage', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!tokenResponse.ok) {
          throw new Error('Failed to fetch token data');
        }

        const data = await tokenResponse.json();
        setTokenData(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching token data:', err);
        setError('Could not load token usage data');
        setLoading(false);
      }
    };

    if (token) {
      fetchTokenData();
    }
  }, [token, user]);

  // Handle password change form
  const handlePasswordChange = (e) => {
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value
    });
  };

  // Submit password change
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordMessage(null);

    // Validation
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      setPasswordLoading(false);
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'New password must be at least 6 characters long' });
      setPasswordLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/users/change-password', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPasswordForm(false);
      } else {
        setPasswordMessage({ type: 'error', text: data.message || 'Failed to change password' });
      }
    } catch (err) {
      console.error('Error changing password:', err);
      setPasswordMessage({ type: 'error', text: 'An error occurred while changing password' });
    } finally {
      setPasswordLoading(false);
    }
  };

  // Hard-coded plan name based on subscription.isTestUser
  // This is a fallback if the plan field isn't available
  const getPlanName = () => {
    if (!user) return 'Unknown';
    
    // If user has subscription data with a plan
    if (user.subscription && user.subscription.plan) {
      switch(user.subscription.plan) {
        case 'test': return 'Test User';
        case 'basic': return 'Basic';
        case 'trial': return 'Trial';
        default: return 'Unknown';
      }
    }
    
    // Fallback for test users without plan field
    if (user.subscription && user.subscription.isTestUser) {
      return 'Test User';
    }
    
    return 'Unknown';
  };
  
  return (
    <div style={{maxWidth: '1000px', margin: '0 auto', padding: '2rem'}}>
      <h1>My Account</h1>
      <p style={{marginBottom: '2rem', color: '#666'}}>
        Manage your account and track your token usage
      </p>
      
      {/* Token Usage Dashboard */}
      <div style={{
        background: 'white', 
        borderRadius: '8px', 
        padding: '1.5rem', 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h2 style={{color: '#8b4513', marginBottom: '1rem'}}>Token Usage</h2>
        
        {loading ? (
          <div style={{textAlign: 'center', padding: '2rem'}}>
            <p>Loading token data...</p>
          </div>
        ) : error ? (
          <div style={{
            padding: '1rem',
            background: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            marginBottom: '1rem'
          }}>
            <p>{error}</p>
          </div>
        ) : tokenData ? (
          <>
            <div style={{marginBottom: '1.5rem'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem'}}>
                <span>Monthly Usage</span>
                <span>{Math.round((tokenData.tokensUsed / tokenData.monthlyTokenLimit) * 100)}%</span>
              </div>
              
              <div style={{height: '12px', background: '#e9ecef', borderRadius: '6px', overflow: 'hidden'}}>
                <div 
                  style={{
                    width: `${Math.min(100, Math.round((tokenData.tokensUsed / tokenData.monthlyTokenLimit) * 100))}%`, 
                    height: '100%', 
                    background: tokenData.tokensUsed / tokenData.monthlyTokenLimit > 0.8 ? '#dc3545' : '#2e7d32',
                    transition: 'width 0.5s ease'
                  }}
                ></div>
              </div>
            </div>
            
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem', textAlign: 'center'}}>
              <div>
                <h4 style={{color: '#6c757d', margin: '0 0 0.5rem 0', fontWeight: 'normal'}}>Used</h4>
                <p style={{fontSize: '1.25rem', margin: 0, fontWeight: 'bold'}}>
                  {tokenData.tokensUsed.toLocaleString()}
                </p>
              </div>
              
              <div>
                <h4 style={{color: '#6c757d', margin: '0 0 0.5rem 0', fontWeight: 'normal'}}>Remaining</h4>
                <p style={{
                  fontSize: '1.25rem', 
                  margin: 0, 
                  fontWeight: 'bold',
                  color: tokenData.tokensUsed / tokenData.monthlyTokenLimit > 0.8 ? '#dc3545' : 'inherit'
                }}>
                  {(tokenData.monthlyTokenLimit - tokenData.tokensUsed).toLocaleString()}
                </p>
              </div>
              
              <div>
                <h4 style={{color: '#6c757d', margin: '0 0 0.5rem 0', fontWeight: 'normal'}}>Monthly Limit</h4>
                <p style={{fontSize: '1.25rem', margin: 0, fontWeight: 'bold'}}>
                  {tokenData.monthlyTokenLimit.toLocaleString()}
                </p>
              </div>
            </div>
            
            {tokenData.tokensUsed / tokenData.monthlyTokenLimit > 0.8 && (
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                background: '#fff3cd',
                borderRadius: '4px',
                color: '#856404',
                fontSize: '0.9rem'
              }}>
                <strong>Notice:</strong> You've used over 80% of your monthly token allocation.
              </div>
            )}
          </>
        ) : (
          <p>No token data available</p>
        )}
        
        <div style={{
          marginTop: '1.5rem',
          fontSize: '0.9rem',
          color: '#6c757d',
          background: '#f8f9fa',
          padding: '0.75rem',
          borderRadius: '4px'
        }}>
          <p style={{margin: '0 0 0.5rem 0'}}><strong>What are tokens?</strong></p>
          <p style={{margin: 0}}>
            Tokens measure the complexity and length of your conversations with Gene. 
            Each message you send and receive uses tokens from your monthly allocation.
            An average research session uses 4,000-6,000 tokens, while generating a family 
            history chapter uses approximately 15,000 tokens.
          </p>
        </div>
      </div>
      
      {/* Account Information */}
      <div style={{
        background: 'white',
        borderRadius: '8px',
        padding: '1.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h2 style={{color: '#8b4513', marginBottom: '1rem'}}>Account Information</h2>
        <div style={{marginBottom: '1rem'}}>
          <p><strong>Name:</strong> {user ? user.name : 'Loading...'}</p>
          <p><strong>Email:</strong> {user ? user.email : 'Loading...'}</p>
          <p><strong>Plan:</strong> {getPlanName()}</p>
        </div>
      </div>

      {/* Password Management Section */}
      <div style={{
        background: 'white',
        borderRadius: '8px',
        padding: '1.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h2 style={{color: '#8b4513', marginBottom: '1rem'}}>Security</h2>
        
        {!showPasswordForm ? (
          <div style={{textAlign: 'center'}}>
            <p style={{color: '#666', marginBottom: '1rem'}}>
              Keep your account secure by updating your password regularly.
            </p>
            <button
              onClick={() => setShowPasswordForm(true)}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#2e7d32',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Change Password
            </button>
          </div>
        ) : (
          <form onSubmit={handlePasswordSubmit}>
            <div style={{marginBottom: '1rem'}}>
              <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 'bold'}}>
                Current Password
              </label>
              <input
                type="password"
                name="currentPassword"
                value={passwordForm.currentPassword}
                onChange={handlePasswordChange}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{marginBottom: '1rem'}}>
              <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 'bold'}}>
                New Password
              </label>
              <input
                type="password"
                name="newPassword"
                value={passwordForm.newPassword}
                onChange={handlePasswordChange}
                required
                minLength="6"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{marginBottom: '1.5rem'}}>
              <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 'bold'}}>
                Confirm New Password
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange}
                required
                minLength="6"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            {passwordMessage && (
              <div style={{
                padding: '0.75rem',
                borderRadius: '4px',
                marginBottom: '1rem',
                background: passwordMessage.type === 'success' ? '#d4edda' : '#f8d7da',
                color: passwordMessage.type === 'success' ? '#155724' : '#721c24',
                border: `1px solid ${passwordMessage.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
              }}>
                {passwordMessage.text}
              </div>
            )}
            
            <div style={{display: 'flex', gap: '1rem'}}>
              <button
                type="submit"
                disabled={passwordLoading}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: passwordLoading ? '#6c757d' : '#2e7d32',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: passwordLoading ? 'not-allowed' : 'pointer',
                  fontSize: '1rem'
                }}
              >
                {passwordLoading ? 'Changing...' : 'Update Password'}
              </button>
              
              <button
                type="button"
                onClick={() => {
                  setShowPasswordForm(false);
                  setPasswordForm({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                  setPasswordMessage(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Back to Research Button */}
      <div style={{textAlign: 'center'}}>
        <Link to="/research" style={{
          display: 'inline-block',
          padding: '0.75rem 1.5rem',
          background: '#8b4513',
          color: 'white',
          textDecoration: 'none',
          borderRadius: '6px',
          fontSize: '1rem'
        }}>
          Back to Research
        </Link>
      </div>
    </div>
  );
};

export default AccountPage;
